import { ReactNode, useState } from 'react';

import { useModal } from '@/hooks/use-modal';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { formatWallClockTime } from '@/utils/date-formatters';

import { DeleteTimeEntryModal } from '../components/modals/delete-time-entry-modal';
import { TimeEntryModal } from '../components/modals/time-entry-modal';
import { CreateWorklogRequest, UpdateWorklogRequest } from '../types/dto/worklogs-summary.dto';
import { EditableTimeEntry, ShortProject, TimeTrack } from '../types/time-tracking';
import { useCreateWorklog, useDeleteWorklog, useUpdateWorklog } from './use-time-track-mutations';

interface UseWorklogEditingParams {
  /** The project context used to populate the time-entry modal's project field. */
  project: ShortProject;
}

interface UseWorklogEditingResult {
  /** Open the edit modal pre-populated with the given track. */
  openEdit: (track: TimeTrack) => void;
  /** Open the delete confirmation modal for the given track. */
  openDelete: (track: TimeTrack) => void;
  /** JSX containing both modals — render once in the host component. */
  modalsElement: ReactNode;
}

/**
 * Shared worklog edit + delete modal plumbing used by ProjectCard (Day view)
 * and ProjectWeekEntries (Week view) so the modal state + mutation calls
 * stay in one place.
 */
export function useWorklogEditing({ project }: UseWorklogEditingParams): UseWorklogEditingResult {
  const { mutateAsync: createWorklog } = useCreateWorklog();
  const { mutateAsync: updateWorklog } = useUpdateWorklog();
  const { mutate: deleteWorklog, isPending: isDeleting } = useDeleteWorklog();

  const { open: isEditOpen, onOpen: openEditModal, onClose: closeEditModal } = useModal();
  const { open: isDeleteOpen, onOpen: openDeleteModal, onClose: closeDeleteModal } = useModal();

  const [editing, setEditing] = useState<EditableTimeEntry | null>(null);

  const defaultProject: DropdownSearchItem = {
    id: String(project.projectId),
    name: project.projectName,
    colorHex: project.colorHex,
    avatarLink: project.logoUrl ?? undefined,
  };

  const openEdit = (track: TimeTrack) => {
    setEditing({
      ...track,
      startTime: formatWallClockTime(track.date),
      project,
    });
    openEditModal();
  };

  const openDelete = (track: TimeTrack) => {
    setEditing(track);
    openDeleteModal();
  };

  const handleSubmit = async (values: CreateWorklogRequest) => {
    if (editing) {
      await updateWorklog({ ...values, id: editing.id } as UpdateWorklogRequest);
    } else {
      await createWorklog(values);
    }
  };

  const handleEditClose = () => {
    closeEditModal();
    setEditing(null);
  };

  const handleDeleteClose = () => {
    closeDeleteModal();
    setEditing(null);
  };

  const modalsElement = (
    <>
      <TimeEntryModal
        open={isEditOpen}
        onClose={handleEditClose}
        defaultProject={defaultProject}
        onSubmit={handleSubmit}
        timeTrack={editing ?? undefined}
      />
      {editing && (
        <DeleteTimeEntryModal
          open={isDeleteOpen}
          onOpen={openDeleteModal}
          onClose={handleDeleteClose}
          isLoading={isDeleting}
          onConfirm={() => {
            deleteWorklog(editing.id, { onSuccess: handleDeleteClose });
          }}
        />
      )}
    </>
  );

  return { openEdit, openDelete, modalsElement };
}
